// Movie Collection Persistence Service
import { isSupabaseConfigured } from '../config/supabase';
import { queuedSupabaseOperation } from '../utils/supabaseWrapper';

/**
 * Save the entire movie collection to Supabase
 */
// Prevent concurrent saves
let saveInProgress = false;

export const saveMovieCollection = async (movies) => {
  const saveId = Math.random().toString(36).substr(2, 9);
  console.log('🔍 saveMovieCollection called with', movies.length, 'movies', `[ID: ${saveId}]`);
  console.log('🔍 isSupabaseConfigured():', isSupabaseConfigured());
  
  // Prevent concurrent saves
  if (saveInProgress) {
    console.log('⚠️ Save already in progress, queuing this request', `[ID: ${saveId}]`);
    // Wait a bit and try again
    await new Promise(resolve => setTimeout(resolve, 100));
    if (saveInProgress) {
      console.log('⚠️ Save still in progress, skipping duplicate save', `[ID: ${saveId}]`);
      return { data: movies, error: null };
    }
  }
  
  saveInProgress = true;
  
  if (!isSupabaseConfigured()) {
    console.warn('⚠️ Supabase not configured, falling back to localStorage');
    localStorage.setItem('movie_collection', JSON.stringify(movies));
    saveInProgress = false;
    return { data: movies, error: null };
  }

  try {
    console.log('💾 Saving movie collection to Supabase...', movies.length, 'movies');
    
    // Clear existing collection first using queued operation
    console.log('🗑️ Clearing existing movie collection...');
    await queuedSupabaseOperation.delete(
      'movie_collection',
      'id=neq.00000000-0000-0000-0000-000000000000', // Delete all rows
      null,
      'Clear movie collection'
    );
    console.log('✅ Existing movie collection cleared');

    // Insert all movies using queued operation
    if (movies.length > 0) {
      console.log('📥 Inserting', movies.length, 'movies into collection...');
      const movieData = movies.map(movie => ({
        movie_id: movie.id,
        movie_data: movie,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));
      
      console.log('🎬 Sample movie data:', movieData[0]);
      
      await queuedSupabaseOperation.insert(
        'movie_collection',
        movieData,
        'movie_collection', // localStorage fallback key
        `Insert ${movies.length} movies`
      );
      
      console.log('✅ Movies inserted successfully');
      console.log('✅ Movie collection saved to Supabase');
      
      // Also save to localStorage as backup
      localStorage.setItem('movie_collection', JSON.stringify(movies));
      return { data: movieData, error: null };
    } else {
      console.log('✅ Empty movie collection saved');
      localStorage.removeItem('movie_collection');
      return { data: [], error: null };
    }
  } catch (error) {
    console.error('❌ Error saving movie collection:', error);
    // Fallback to localStorage
    localStorage.setItem('movie_collection', JSON.stringify(movies));
    return { data: null, error };
  } finally {
    saveInProgress = false;
  }
};

/**
 * Load the movie collection from Supabase
 */
export const loadMovieCollection = async () => {
  if (!isSupabaseConfigured()) {
    console.warn('Supabase not configured, loading from localStorage');
    const saved = localStorage.getItem('movie_collection');
    return saved ? JSON.parse(saved) : [];
  }

  try {
    console.log('📂 Loading movie collection from Supabase...');
    
    const data = await queuedSupabaseOperation.select(
      'movie_collection',
      'select=movie_data&order=created_at.asc',
      'movie_collection', // localStorage fallback key
      'Load movie collection'
    );

    const movies = data ? data.map(row => row.movie_data) : [];
    console.log('✅ Movie collection loaded from Supabase:', movies.length, 'movies');
    
    // Also save to localStorage as backup
    if (movies.length > 0) {
      localStorage.setItem('movie_collection', JSON.stringify(movies));
    }
    
    return movies;
  } catch (error) {
    console.error('❌ Error loading movie collection from Supabase:', error);
    // Fallback to localStorage
    const saved = localStorage.getItem('movie_collection');
    const fallbackMovies = saved ? JSON.parse(saved) : [];
    console.log('🔄 Fallback to localStorage:', fallbackMovies.length, 'movies');
    return fallbackMovies;
  }
};

/**
 * Add a single movie to the collection
 */
export const addMovieToCollection = async (movie, existingMovies) => {
  const updatedMovies = [...existingMovies, movie];
  await saveMovieCollection(updatedMovies);
  return updatedMovies;
};

/**
 * Remove a movie from the collection
 */
export const removeMovieFromCollection = async (movieId, existingMovies) => {
  const updatedMovies = existingMovies.filter(m => m.id !== movieId);
  await saveMovieCollection(updatedMovies);
  return updatedMovies;
};

/**
 * Clear the entire collection
 */
export const clearMovieCollection = async () => {
  await saveMovieCollection([]);
  return [];
};

